import { Frame } from '../track/frames';
import { TrackRuntime } from '../track/runtime';
import {
  ARROW_PROFILE,
  PRINT_ARROW_PROFILE,
  ProfileLoop,
  ProfilePoint,
  rackDeckTopY,
  trackProfiles,
  TrackProfileParams,
} from './profile';
import { mergeMeshes, SweepMesh, sweepProfile } from './sweep';
import { type2Layout, Type2ShapeParams, type2TrackProfiles } from './type2Shape';

/** How far each tooth base sinks into the deck so boolean union is solid. */
const TOOTH_EMBED_M = 0.012;

/** Print thickens rails vs render by this factor (legacy Arrow print profile). */
const PRINT_RAIL_THICKEN = PRINT_ARROW_PROFILE.railRadius / ARROW_PROFILE.railRadius;

export type TrackType = 'type1' | 'type2';

/** Track cross-section + gear rack knobs (sidebar “Track Shape”). */
export interface TrackShapeSettings {
  /** Type 1 = rails + flat beam + triangle. Type 2 = V-spine from the shape editor. */
  trackType: TrackType;
  /** Diameter of the two side track beams (m). */
  railDiameter: number;
  /** Centre-to-centre spacing of the two side track beams (m). */
  railSpacing: number;
  /** Vertical height of the continuous flat beam joining the rails (m). */
  flatBeamHeight: number;
  /** Top width of the solid triangle where it meets the flat beam (m). */
  triangleWidth: number;
  /** Vertical height of the solid triangle below the flat beam (m). */
  triangleHeight: number;
  /** Type 2: centre-to-centre spacing of the top rail circles (m). */
  type2TopGap: number;
  /** Type 2: width of the rack/beam stack (m). */
  type2BottomWidth: number;
  /** Type 2: vertical drop from rail centres to the top of the rack-top square (m). */
  type2VHeight: number;
  /** Type 2: width at the top of the rack-top section; widen it to reach the V arms (m). */
  type2RackTopWidth: number;
  /** Type 2: height of the square above the rack-bottom section (m). */
  type2RackTopHeight: number;
  /** Type 2: height of the rack-bottom square (m). */
  type2RackBottomHeight: number;
  /** Type 2: height of the beam square under the rack (m). */
  type2BeamHeight: number;
  /** Type 2: diameter of the two top circles (m). */
  type2CircleDiameter: number;
  /** Type 2: V-line / inner-wall thickness (m). */
  type2Thickness: number;
  /** Type 2: fill the sides between the rail circles and the rack top solidly. */
  type2SolidWebs: boolean;
  /** Type 2: with open sides, the gap along the track between two plates (m). */
  type2PlateGap: number;
  /** Type 2: with open sides, how thick each plate is along the track (m). */
  type2PlateThickness: number;
  /** When false, no rack teeth are meshed. */
  enabled: boolean;
  /** Centre-to-centre spacing of teeth along the track (m). */
  pitch: number;
  /** How far teeth rise above the deck top face (m). */
  toothHeight: number;
  /** Lateral width of teeth (m); clamped to spine/rack strip width when meshing. */
  toothWidth: number;
  /** Along-track base length of each triangular tooth (m); clamped below pitch. */
  toothThickness: number;
}

/** @deprecated Use {@link TrackShapeSettings}. */
export type GearRackSettings = TrackShapeSettings;

export const DEFAULT_TRACK_SHAPE: TrackShapeSettings = {
  trackType: 'type1',
  railDiameter: ARROW_PROFILE.railRadius * 2,
  railSpacing: ARROW_PROFILE.gauge,
  flatBeamHeight: ARROW_PROFILE.flatBeamHeight,
  triangleWidth: ARROW_PROFILE.triangleWidth,
  triangleHeight: ARROW_PROFILE.triangleHeight,
  type2TopGap: ARROW_PROFILE.gauge,
  type2BottomWidth: 0.2,
  type2VHeight: ARROW_PROFILE.triangleHeight,
  type2RackTopWidth: 0.2,
  type2RackTopHeight: 0.08,
  type2RackBottomHeight: 0.16,
  type2BeamHeight: ARROW_PROFILE.flatBeamHeight,
  type2CircleDiameter: ARROW_PROFILE.railRadius * 2,
  type2Thickness: 0.04,
  type2SolidWebs: true,
  type2PlateGap: 0.3,
  type2PlateThickness: 0.04,
  enabled: true,
  pitch: 0.12,
  toothHeight: 0.05,
  toothWidth: 0.22,
  toothThickness: 0.05,
};

/** @deprecated Use {@link DEFAULT_TRACK_SHAPE}. */
export const DEFAULT_GEAR_RACK = DEFAULT_TRACK_SHAPE;

/** Fill missing knobs so save/export always writes a complete object. */
export function normalizeTrackShapeSettings(
  partial: Partial<TrackShapeSettings> | null | undefined,
): TrackShapeSettings {
  const out: TrackShapeSettings = { ...DEFAULT_TRACK_SHAPE };
  if (!partial || typeof partial !== 'object') return out;
  if (
    typeof partial.railDiameter === 'number' &&
    Number.isFinite(partial.railDiameter) &&
    partial.railDiameter > 0
  ) {
    out.railDiameter = partial.railDiameter;
  }
  if (
    typeof partial.railSpacing === 'number' &&
    Number.isFinite(partial.railSpacing) &&
    partial.railSpacing > 0
  ) {
    out.railSpacing = partial.railSpacing;
  }
  // Legacy flatBeamWidth → flatBeamHeight
  const legacyFlat =
    typeof (partial as { flatBeamWidth?: number }).flatBeamWidth === 'number'
      ? (partial as { flatBeamWidth: number }).flatBeamWidth
      : undefined;
  if (
    typeof partial.flatBeamHeight === 'number' &&
    Number.isFinite(partial.flatBeamHeight) &&
    partial.flatBeamHeight > 0
  ) {
    out.flatBeamHeight = partial.flatBeamHeight;
  } else if (legacyFlat != null && Number.isFinite(legacyFlat) && legacyFlat > 0) {
    out.flatBeamHeight = legacyFlat;
  }
  if (
    typeof partial.triangleWidth === 'number' &&
    Number.isFinite(partial.triangleWidth) &&
    partial.triangleWidth > 0
  ) {
    out.triangleWidth = partial.triangleWidth;
  }
  if (
    typeof partial.triangleHeight === 'number' &&
    Number.isFinite(partial.triangleHeight) &&
    partial.triangleHeight > 0
  ) {
    out.triangleHeight = partial.triangleHeight;
  }
  // Keep the triangle from exceeding the rail spacing.
  if (out.triangleWidth > out.railSpacing) out.triangleWidth = out.railSpacing;
  if (typeof partial.enabled === 'boolean') out.enabled = partial.enabled;
  if (typeof partial.pitch === 'number' && Number.isFinite(partial.pitch) && partial.pitch > 0) {
    out.pitch = partial.pitch;
  }
  if (
    typeof partial.toothHeight === 'number' &&
    Number.isFinite(partial.toothHeight) &&
    partial.toothHeight > 0
  ) {
    out.toothHeight = partial.toothHeight;
  }
  if (
    typeof partial.toothWidth === 'number' &&
    Number.isFinite(partial.toothWidth) &&
    partial.toothWidth > 0
  ) {
    out.toothWidth = partial.toothWidth;
  }
  if (
    typeof partial.toothThickness === 'number' &&
    Number.isFinite(partial.toothThickness) &&
    partial.toothThickness > 0
  ) {
    out.toothThickness = partial.toothThickness;
  }
  if (partial.trackType === 'type1' || partial.trackType === 'type2') {
    out.trackType = partial.trackType;
  }
  if (typeof partial.type2TopGap === 'number' && Number.isFinite(partial.type2TopGap) && partial.type2TopGap > 0) {
    out.type2TopGap = partial.type2TopGap;
  }
  if (
    typeof partial.type2BottomWidth === 'number' &&
    Number.isFinite(partial.type2BottomWidth) &&
    partial.type2BottomWidth >= 0
  ) {
    out.type2BottomWidth = partial.type2BottomWidth;
  }
  if (typeof partial.type2VHeight === 'number' && Number.isFinite(partial.type2VHeight) && partial.type2VHeight >= 0) {
    out.type2VHeight = partial.type2VHeight;
  }
  if (
    typeof partial.type2RackTopWidth === 'number' &&
    Number.isFinite(partial.type2RackTopWidth) &&
    partial.type2RackTopWidth >= 0
  ) {
    out.type2RackTopWidth = partial.type2RackTopWidth;
  } else {
    // Predates the slider: the rack top was always flush with the stem.
    out.type2RackTopWidth = out.type2BottomWidth;
  }
  if (
    typeof partial.type2RackTopHeight === 'number' &&
    Number.isFinite(partial.type2RackTopHeight) &&
    partial.type2RackTopHeight >= 0
  ) {
    out.type2RackTopHeight = partial.type2RackTopHeight;
  }
  if (
    typeof partial.type2RackBottomHeight === 'number' &&
    Number.isFinite(partial.type2RackBottomHeight) &&
    partial.type2RackBottomHeight >= 0
  ) {
    out.type2RackBottomHeight = partial.type2RackBottomHeight;
  }
  if (
    typeof partial.type2BeamHeight === 'number' &&
    Number.isFinite(partial.type2BeamHeight) &&
    partial.type2BeamHeight >= 0
  ) {
    out.type2BeamHeight = partial.type2BeamHeight;
  }
  if (
    typeof partial.type2CircleDiameter === 'number' &&
    Number.isFinite(partial.type2CircleDiameter) &&
    partial.type2CircleDiameter > 0
  ) {
    out.type2CircleDiameter = partial.type2CircleDiameter;
  }
  if (
    typeof partial.type2Thickness === 'number' &&
    Number.isFinite(partial.type2Thickness) &&
    partial.type2Thickness > 0
  ) {
    out.type2Thickness = partial.type2Thickness;
  }
  if (typeof partial.type2SolidWebs === 'boolean') {
    out.type2SolidWebs = partial.type2SolidWebs;
  }
  if (
    typeof partial.type2PlateGap === 'number' &&
    Number.isFinite(partial.type2PlateGap) &&
    partial.type2PlateGap > 0
  ) {
    out.type2PlateGap = partial.type2PlateGap;
  }
  if (
    typeof partial.type2PlateThickness === 'number' &&
    Number.isFinite(partial.type2PlateThickness) &&
    partial.type2PlateThickness > 0
  ) {
    out.type2PlateThickness = partial.type2PlateThickness;
  } else {
    // Predates the slider: plates were as thick as the V lines.
    out.type2PlateThickness = out.type2Thickness;
  }
  return out;
}

/** @deprecated Use {@link normalizeTrackShapeSettings}. */
export const normalizeGearRackSettings = normalizeTrackShapeSettings;

/** Live-view track profile with the configured rail / triangle sizes. */
export function renderTrackProfile(shapeIn: Partial<TrackShapeSettings> | null | undefined): TrackProfileParams {
  const shape = normalizeTrackShapeSettings(shapeIn);
  return {
    ...ARROW_PROFILE,
    railRadius: Math.max(shape.railDiameter / 2, 1e-4),
    gauge: Math.max(shape.railSpacing, 1e-3),
    flatBeamHeight: Math.max(shape.flatBeamHeight, 1e-4),
    triangleWidth: Math.max(shape.triangleWidth, 1e-3),
    triangleHeight: Math.max(shape.triangleHeight, 1e-3),
  };
}

/** Print track profile: configured rails, thickened for min feature size. */
export function printTrackProfile(shapeIn: Partial<TrackShapeSettings> | null | undefined): TrackProfileParams {
  const shape = normalizeTrackShapeSettings(shapeIn);
  const r = Math.max(shape.railDiameter / 2, 1e-4);
  const flatThicken = PRINT_ARROW_PROFILE.flatBeamHeight / ARROW_PROFILE.flatBeamHeight;
  return {
    ...PRINT_ARROW_PROFILE,
    railRadius: r * PRINT_RAIL_THICKEN,
    gauge: Math.max(shape.railSpacing, 1e-3),
    flatBeamHeight: Math.max(shape.flatBeamHeight, 1e-4) * flatThicken,
    triangleWidth: Math.max(shape.triangleWidth, 1e-3),
    triangleHeight: Math.max(shape.triangleHeight, 1e-3),
  };
}

/** Type 2 cross-section knobs pulled out of the shape settings. */
export function type2ParamsOf(
  shape: TrackShapeSettings,
  thicken = 1,
): Type2ShapeParams {
  return {
    topGap: shape.type2TopGap,
    bottomWidth: shape.type2BottomWidth,
    vHeight: shape.type2VHeight,
    rackTopWidth: shape.type2RackTopWidth,
    rackTopHeight: shape.type2RackTopHeight,
    rackBottomHeight: shape.type2RackBottomHeight,
    beamHeight: shape.type2BeamHeight,
    circleDiameter: shape.type2CircleDiameter * thicken,
    thickness: shape.type2Thickness * thicken,
  };
}

/** Location + size of the Type 2 beam-part click-fit connector (print-thickened). */
export interface Type2BeamJointGeom {
  /** Beam-part centre offset from the rail line along +up (negative = below rails). */
  centerY: number;
  /** Half width of the beam across the track (right axis). */
  halfW: number;
  /** Half height of the beam square (up axis). */
  halfH: number;
}

/**
 * Beam-part connector geometry for Track Type 2, matched to the printed spine
 * (same thickening as {@link trackLoopsForShape} 'print'). Returns null when the
 * shape is not Type 2 or the beam collapses to nothing — callers then fall back
 * to the round spine connector.
 */
export function type2BeamJointGeom(
  shapeIn: Partial<TrackShapeSettings> | null | undefined,
): Type2BeamJointGeom | null {
  const shape = normalizeTrackShapeSettings(shapeIn);
  if (shape.trackType !== 'type2') return null;
  const L = type2Layout(type2ParamsOf(shape, PRINT_RAIL_THICKEN));
  const halfW = (L.rightX - L.leftX) / 2;
  const halfH = (L.rackBottomY - L.beamBottomY) / 2;
  if (halfW < 1e-4 || halfH < 1e-4) return null;
  return { centerY: (L.rackBottomY + L.beamBottomY) / 2, halfW, halfH };
}

/**
 * Rail + spine loops for the active track type (render or print). `plates` is
 * non-empty only for a Type 2 shape with open sides: those loops are not part of
 * the continuous sweep, {@link buildType2PlateMeshes} places them along the track.
 */
export function trackLoopsForShape(
  shapeIn: Partial<TrackShapeSettings> | null | undefined,
  kind: 'render' | 'print',
  railSegments?: number,
): { rails: ProfileLoop[]; spine: ProfileLoop[]; plates: ProfileLoop[] } {
  const shape = normalizeTrackShapeSettings(shapeIn);
  const segments = railSegments ?? (kind === 'print' ? 14 : 10);
  switch (shape.trackType) {
    case 'type1': {
      const p = kind === 'print' ? printTrackProfile(shape) : renderTrackProfile(shape);
      return { ...trackProfiles(p, segments), plates: [] };
    }
    case 'type2': {
      const thicken = kind === 'print' ? PRINT_RAIL_THICKEN : 1;
      return type2TrackProfiles(type2ParamsOf(shape, thicken), segments, shape.type2SolidWebs);
    }
    default: {
      const _never: never = shape.trackType;
      throw new Error(`Unhandled track type: ${_never}`);
    }
  }
}

/**
 * Along-track thickness of one V-side plate, and the centre-to-centre spacing.
 * Print thickens the plates like the rest of the section, so they stay printable.
 */
export function type2PlateSpacing(
  shape: TrackShapeSettings,
  kind: 'render' | 'print',
): { thickness: number; pitch: number } {
  const thicken = kind === 'print' ? PRINT_RAIL_THICKEN : 1;
  const thickness = Math.max(shape.type2PlateThickness * thicken, 1e-3);
  return { thickness, pitch: thickness + Math.max(shape.type2PlateGap, 1e-3) };
}

/** Sampling step along one plate; plates are short, so this is just curvature. */
const PLATE_SAMPLE_STEP = 0.04;

/** A few frames across one plate so it still follows a banked curve. */
function plateFrames(runtime: TrackRuntime, a: number, b: number): Frame[] {
  const n = Math.max(1, Math.ceil((b - a) / PLATE_SAMPLE_STEP));
  const frames: Frame[] = [];
  for (let i = 0; i <= n; i++) frames.push(runtime.frameAt(a + ((b - a) * i) / n));
  return frames;
}

/**
 * V-side plates over `[s0, s1]`. With the sides not solid, each side web sweeps
 * only in short spans placed from s = 0 on a global pitch, so chunk remeshes stay
 * seamless; a plate straddling the interval is clipped to it, ending flush on the
 * same plane as the track (same trick as the rack teeth).
 */
export function buildType2PlateMeshes(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  shapeIn: Partial<TrackShapeSettings> | null | undefined,
  kind: 'render' | 'print',
): SweepMesh[] {
  const shape = normalizeTrackShapeSettings(shapeIn);
  if (shape.trackType !== 'type2' || shape.type2SolidWebs) return [];
  const L = runtime.totalLength;
  if (L < 1e-3 || s1 <= s0 + 1e-6) return [];
  const { plates } = trackLoopsForShape(shape, kind);
  if (plates.length === 0) return [];

  const { thickness, pitch } = type2PlateSpacing(shape, kind);
  const half = thickness / 2;
  const closed = runtime.centerline.track.closed;
  const meshes: SweepMesh[] = [];
  // First centre that could overlap [s0, s1] (not merely lie inside it).
  const first = Math.floor((s0 - half + 1e-9) / pitch) * pitch + pitch / 2;
  for (let c = first; c < s1 + half - 1e-9; c += pitch) {
    if (!closed && (c - half < -1e-6 || c + half > L + 1e-6)) continue;
    const a = Math.max(c - half, s0);
    const b = Math.min(c + half, s1);
    // Skip the sliver a cut can leave behind rather than print a paper-thin rib.
    if (b - a < thickness * 0.25) continue;
    const frames = plateFrames(runtime, a, b);
    for (const loop of plates) meshes.push(sweepProfile(frames, loop, { capEnds: true }));
  }
  return meshes;
}

/** Where the rack teeth sit in the cross-section, per track type. */
export interface RackBand {
  /** Tooth tip. */
  apexY: number;
  /** Tooth base, before the embed that welds it to the track. */
  baseY: number;
  /** How far the base sinks in so the boolean union stays solid. */
  embed: number;
  /** Half the lateral width the teeth may use. */
  halfWidth: number;
}

/**
 * Type 1 seats the teeth on the flat-beam deck, `toothHeight` tall. Type 2 puts
 * them in the spine instead, filling the rack-top and rack-bottom sections — so
 * their full height is those two heights added together and `toothHeight` does
 * not apply.
 */
export function rackBand(rack: TrackShapeSettings, track: TrackProfileParams): RackBand {
  if (rack.trackType === 'type2') {
    const L = type2Layout(type2ParamsOf(rack));
    // Teeth are a constant-width extrusion, so they have to fit the narrowest
    // point of the band — the rack-top section may taper either way.
    const narrowest =
      Math.min(L.rackTopRightX - L.rackTopLeftX, L.rightX - L.leftX) + L.thickness;
    return {
      apexY: L.vBottomY,
      baseY: L.rackBottomY,
      // Sink into the rack-bottom bar without punching out its underside.
      embed: Math.min(TOOTH_EMBED_M, L.thickness * 0.4),
      halfWidth: Math.min(rack.toothWidth, narrowest * 0.95) / 2,
    };
  }
  const deckTop = rackDeckTopY(track);
  return {
    apexY: deckTop + rack.toothHeight,
    baseY: deckTop,
    embed: TOOTH_EMBED_M,
    halfWidth: Math.min(rack.toothWidth, track.spineWidth * 0.95) / 2,
  };
}

/** Local (tangent, up) corners of one isosceles tooth in the rack band. */
export function rackToothTriangle(
  rack: TrackShapeSettings,
  track: TrackProfileParams,
): { baseBack: { t: number; y: number }; baseFront: { t: number; y: number }; apex: { t: number; y: number } } {
  const pitch = Math.max(rack.pitch, 1e-3);
  const half = Math.min(Math.max(rack.toothThickness, 1e-4), pitch * 0.9) / 2;
  const band = rackBand(rack, track);
  return {
    baseBack: { t: -half, y: band.baseY },
    baseFront: { t: half, y: band.baseY },
    apex: { t: 0, y: band.apexY },
  };
}

/**
 * Triangle in the (right′, up) profile plane of the lateral extrude frames,
 * where right′ = −tan (so +x is toward the back of the tooth).
 * CCW, closed by sweep. Base is embedded slightly into the track.
 */
export function rackToothProfileLoop(
  rack: TrackShapeSettings,
  track: TrackProfileParams,
): ProfileLoop {
  const pitch = Math.max(rack.pitch, 1e-3);
  const half = Math.min(Math.max(rack.toothThickness, 1e-4), pitch * 0.9) / 2;
  const band = rackBand(rack, track);
  const y0 = band.baseY - band.embed;
  return {
    points: [
      { x: -half, y: y0 },
      { x: half, y: y0 },
      { x: 0, y: band.apexY },
    ],
  };
}

/**
 * Bottom-land (valley) arc lengths sit at n·pitch, between tooth centres at
 * n·pitch + pitch/2. Print cuts can snap to the nearest valley so a chunk
 * boundary never slices through a tooth.
 */
export function nearestToothBottom(s: number, pitch: number): number {
  const p = Math.max(pitch, 1e-3);
  return Math.round(s / p) * p;
}

/**
 * Place discrete triangular rack teeth along `[s0, s1]` using global pitch from
 * s=0 so chunk remeshes stay seamless. Teeth that straddle a chunk boundary are
 * clipped so the rack ends on the same plane as the track. Each tooth is a
 * watertight prism (side-view polygon extruded laterally) via capped sweepProfile.
 */
export function buildGearRackMeshes(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  rackIn: Partial<TrackShapeSettings> | null | undefined,
  track: TrackProfileParams,
): SweepMesh[] {
  const rack = normalizeTrackShapeSettings(rackIn);
  if (!rack.enabled) return [];
  const L = runtime.totalLength;
  if (L < 1e-3 || s1 <= s0 + 1e-6) return [];

  // Type 2 collapses the band when both rack sections are zero-height.
  const band = rackBand(rack, track);
  if (band.apexY - band.baseY < 1e-6 || band.halfWidth < 1e-6) return [];

  const pitch = Math.max(rack.pitch, 1e-3);
  const thickness = Math.min(Math.max(rack.toothThickness, 1e-4), pitch * 0.9);
  const half = thickness / 2;
  const closed = runtime.centerline.track.closed;
  const pieces: SweepMesh[] = [];

  // First centre that could overlap [s0, s1] (not merely lie inside it).
  const first = Math.floor((s0 - half + 1e-9) / pitch) * pitch + pitch / 2;
  for (let s = first; s < s1 + half - 1e-9; s += pitch) {
    if (s + half <= s0 + 1e-9 || s - half >= s1 - 1e-9) continue;
    if (!closed && (s - half < -1e-6 || s + half > L + 1e-6)) continue;
    const mesh = triangularToothMesh(runtime, s, rack, track, band, s0, s1);
    if (mesh) pieces.push(mesh);
  }
  return pieces;
}

/** Merge rack teeth for an interval into one mesh (or empty). */
export function buildGearRackMesh(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  rack: Partial<TrackShapeSettings> | null | undefined,
  track: TrackProfileParams,
): SweepMesh {
  const parts = buildGearRackMeshes(runtime, s0, s1, rack, track);
  if (parts.length === 0) {
    return { positions: new Float32Array(0), normals: new Float32Array(0), indices: new Uint32Array(0) };
  }
  return mergeMeshes(parts);
}

function triangularToothMesh(
  runtime: TrackRuntime,
  s: number,
  rack: TrackShapeSettings,
  track: TrackProfileParams,
  band: RackBand,
  clipS0: number,
  clipS1: number,
): SweepMesh | null {
  const f = runtime.frameAt(s);
  let loop = rackToothProfileLoop(rack, track);
  // Profile +x is toward −tan (decreasing s), so world s = centre − x.
  const xMin = s - clipS1;
  const xMax = s - clipS0;
  let minX = Infinity;
  let maxX = -Infinity;
  for (const p of loop.points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
  }
  if (xMin > minX + 1e-9 || xMax < maxX - 1e-9) {
    const clipped = clipProfileLoopToXRange(loop, xMin, xMax);
    if (!clipped) return null;
    loop = clipped;
  }
  const frames = lateralToothFrames(f, band.halfWidth);
  return sweepProfile(frames, loop, { capEnds: true });
}

/** Sutherland–Hodgman clip of a convex CCW loop against the strip x ∈ [xMin, xMax]. */
function clipProfileLoopToXRange(loop: ProfileLoop, xMin: number, xMax: number): ProfileLoop | null {
  const clipped = clipPolyX(
    clipPolyX(loop.points, (x) => x >= xMin - 1e-12, xMin),
    (x) => x <= xMax + 1e-12,
    xMax,
  );
  const points = dedupeProfilePoints(clipped);
  if (points.length < 3) return null;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  if (Math.abs(area) < 1e-12) return null;
  return { points };
}

function clipPolyX(
  pts: ProfilePoint[],
  inside: (x: number) => boolean,
  bound: number,
): ProfilePoint[] {
  if (pts.length === 0) return [];
  const out: ProfilePoint[] = [];
  for (let i = 0; i < pts.length; i++) {
    const cur = pts[i];
    const prev = pts[(i + pts.length - 1) % pts.length];
    const curIn = inside(cur.x);
    const prevIn = inside(prev.x);
    if (curIn) {
      if (!prevIn) out.push(intersectAtX(prev, cur, bound));
      out.push(cur);
    } else if (prevIn) {
      out.push(intersectAtX(prev, cur, bound));
    }
  }
  return out;
}

function intersectAtX(a: ProfilePoint, b: ProfilePoint, x: number): ProfilePoint {
  const dx = b.x - a.x;
  const t = Math.abs(dx) < 1e-18 ? 0 : (x - a.x) / dx;
  return { x, y: a.y + (b.y - a.y) * t };
}

function dedupeProfilePoints(pts: ProfilePoint[]): ProfilePoint[] {
  if (pts.length === 0) return [];
  const out: ProfilePoint[] = [];
  for (const p of pts) {
    const prev = out[out.length - 1];
    if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < 1e-10) continue;
    out.push(p);
  }
  if (out.length > 1) {
    const first = out[0];
    const last = out[out.length - 1];
    if (Math.hypot(first.x - last.x, first.y - last.y) < 1e-10) out.pop();
  }
  return out;
}

function lateralToothFrames(f: Frame, halfWidth: number): Frame[] {
  const tan = f.right.clone();
  const up = f.up.clone();
  const right = f.tan.clone().multiplyScalar(-1);
  return [
    {
      s: f.s,
      pos: f.pos.clone().addScaledVector(f.right, -halfWidth),
      tan: tan.clone(),
      up: up.clone(),
      right: right.clone(),
      rollDeg: f.rollDeg,
      railPos: f.railPos.clone().addScaledVector(f.right, -halfWidth),
    },
    {
      s: f.s,
      pos: f.pos.clone().addScaledVector(f.right, halfWidth),
      tan: tan.clone(),
      up: up.clone(),
      right: right.clone(),
      rollDeg: f.rollDeg,
      railPos: f.railPos.clone().addScaledVector(f.right, halfWidth),
    },
  ];
}
